import { Application } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import swaggerJsdoc from 'swagger-jsdoc';
import swaggerUi from 'swagger-ui-express';
import path from 'path';
import { DocumentationOptions } from '../../types';
import { createPathResolver } from '../../utils';

/**
 * Setup Swagger documentation
 *
 * @param app Express application
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Documentation options
 */
export async function setupSwagger(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: DocumentationOptions = {}
): Promise<void> {
  try {
    // Get configuration for Swagger
    const title = options.title || config.get<string>('api.docs.title', 'API Documentation');
    const version = options.version || config.get<string>('api.docs.version', '1.0.0');
    const description = options.description || config.get<string>('api.docs.description', 'API Documentation');
    const endpoint = options.endpoint || config.get<string>('api.docs.endpoint', '/api-docs');

    // Create path resolver for finding API files
    const pathResolver = createPathResolver(logger);

    // Determine API paths to scan
    let apiPaths = options.apiPaths || config.get<string[]>('api.docs.paths', []);

    // If no paths are specified, use default paths
    if (!apiPaths || apiPaths.length === 0) {
      const srcDir = pathResolver.resolveFromRoot('src');

      apiPaths = [
        path.join(srcDir, 'routes/**/*.ts'),
        path.join(srcDir, 'routes/**/*.js'),
        path.join(srcDir, 'models/**/*.ts'),
        path.join(srcDir, 'models/**/*.js'),
        path.join(srcDir, 'controllers/**/*.ts'),
        path.join(srcDir, 'controllers/**/*.js')
      ];
    }

    logger.debug('Generating Swagger documentation', {
      title,
      version,
      endpoint,
      apiPaths
    });

    // OAuth2 configuration
    const useOAuth2 = options.oauth2?.enabled !== false &&
      config.get<boolean>('auth.azure.enabled', false);

    let securitySchemes: Record<string, any> = {
      bearerAuth: {
        type: 'http',
        scheme: 'bearer',
        bearerFormat: 'JWT',
        description: 'Enter your JWT token'
      }
    };

    let swaggerUiOptions: Record<string, any> = {
      explorer: true,
      customCss: options.customCss || '.swagger-ui .topbar { display: none }',
      customSiteTitle: title
    };

    // If OAuth2 is enabled, add configuration
    if (useOAuth2) {
      const authType = config.get<string>('auth.type', 'none');

      if (authType === 'azure') {
        // Get Azure AD configuration
        const tenantId = config.get<string>('auth.azure.tenantId', '');
        const clientId = options.oauth2?.clientId || config.get<string>('auth.azure.clientId', '');
        const apiScope = config.get<string>('auth.azure.apiScope', 'api/.default');
        const scopes = options.oauth2?.scopes || [`${apiScope}`];

        // Auth server URL
        const authServerUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0`;

        // Add Azure AD security scheme
        securitySchemes.azureAD = {
          type: 'oauth2',
          flows: {
            implicit: {
              authorizationUrl: `${authServerUrl}/authorize`,
              scopes: scopes.reduce((acc, scope) => {
                acc[scope] = `Access to ${scope}`;
                return acc;
              }, {} as Record<string, string>)
            }
          }
        };

        // Add OAuth2 configuration for Swagger UI
        swaggerUiOptions.swaggerOptions = {
          persistAuthorization: true,
          oauth2RedirectUrl: `${config.get<string>('api.baseUrl', '')}/api-docs/oauth2-redirect.html`,
          initOAuth: {
            clientId: clientId,
            appName: options.oauth2?.appName || title,
            scopes: scopes,
            scopeSeparator: ' ',
            usePkceWithAuthorizationCodeGrant: true
          }
        };
      }
    }

    // Create Swagger spec
    const swaggerOptions = {
      definition: {
        openapi: '3.0.0',
        info: {
          title,
          version,
          description
        },
        servers: [{
          url: config.get<string>('api.basePath', ''),
          description: 'API Server'
        }],
        components: {
          securitySchemes
        },
        security: [
          {
            bearerAuth: []
          }
        ]
      },
      apis: apiPaths
    };

    const swaggerSpec = swaggerJsdoc(swaggerOptions);

    // Setup Swagger UI
    app.use(endpoint, swaggerUi.serve, swaggerUi.setup(swaggerSpec, swaggerUiOptions));
    logger.info(`Swagger documentation available at ${endpoint}`);

    // Add an endpoint to get the Swagger JSON
    app.get(`${endpoint}.json`, (req, res) => {
      res.setHeader('Content-Type', 'application/json');
      res.json(swaggerSpec);
    });
    logger.debug(`Swagger JSON available at ${endpoint}.json`);

  } catch (error) {
    logger.error('Failed to setup Swagger documentation', error as Error);
    throw error;
  }
}
